import { useTranslation } from "react-i18next";
import {
	BillingInfo,
	FrontendOrderItem,
	FrontendProduct,
	FrontendSampleSpecification,
	FrontendSampleTestBatch,
	FrontendUserInfo,
	FrontendUserLicense,
	ShippingInfo,
} from "node-library/dist/portal";
import ItemPreviewTable from "./itemPreviewTable";
import {
	CustomCheckbox,
	CustomNumberInput,
	CustomNumberInputDropdown,
	CustomPriceInput,
	RowDropdown,
	StandardRadioGroup,
} from "../../../common-components/inputs";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Autocomplete, Card, Divider, TextField } from "@mui/material";
import {
	SkyBlueButton,
	CustomIconButton,
	DropdownButton,
	BlueButton,
	FlexDropdownButton,
	TextButton,
} from "../../../common-components/button";
import {
	AddIcon,
	AlertIcon,
	SaveIcon,
	SmallEditIcon,
} from "../../../common-components/svgIcons";
import ClearIcon from "@mui/icons-material/Clear";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import React, { useEffect, useRef, useState } from "react";
import DiscardOrderConfirmModal from "../../../common-components/modals/discardOrderConfirmModal";
import KeyboardArrowLeftIconRounded from "@mui/icons-material/KeyboardArrowLeftRounded";
import NewOrderSkeletonUI from "./skeletonUI/newOrderSkeleton";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	DiscountUnit,
	Severity,
	TaxOptions,
} from "node-library/dist/constants";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import CurrencySelector from "../../../common-components/currencySelector";
import { HANDLING_DEFAULT_DESCRIPTION } from "node-library/dist/description";
import {
	calculateDiscount,
	calculateItemPrice,
	formatPrice,
	getTaxOptionTranslationText,
} from "../../../helpers/sampleHelper";

interface Props {
	selectedUser: FrontendUserInfo;
	billingInfo: BillingInfo;
	shippingInfo: ShippingInfo;
	licenseLoading: boolean;
	isInAdminView?: boolean;

	goBackToOrderDetailsPage?: () => void;
	goBackToClientDetail: () => void;
	itemList: Array<FrontendOrderItem>;
	deleteItem: (itemId: string) => void;
	deleteHandlingFee: () => void;
	deleteShippingFee: () => void;
	goToUpdateItem: (item: FrontendOrderItem) => void;
	goToAddNewItem: () => void;
	goBackToAddItemPage: () => void;
	goToAddShippingHandling: () => void;
	goToSubmitOrder: (saveLater: boolean) => void;
	discardOrder: () => Promise<void>;
	saveOrderAndEditLater: () => Promise<void>;
	quoteNumber: string;
	handlingFee: number;
	handlingDescription: string;
	shippingFee: number;
	shippingDescription: string;
	fullServiceShippingSolution: boolean;
	discount: number;
	discountUnit: DiscountUnit;
	tax: number;
	currency: string; // or a more specific type if you have one
	total: number;
	subtotal: number;
	itemTotal: number;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function OrderReview(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		selectedUser,
		billingInfo,
		shippingInfo,
		itemList,
		deleteItem,
		goToUpdateItem,
		goBackToAddItemPage,
		goToAddShippingHandling,
		goBackToClientDetail,
		goToSubmitOrder,
		discardOrder,
		saveOrderAndEditLater,
		handlingFee,
		shippingFee,
		discount,
		discountUnit,
		tax,
		currency,
		total,
		subtotal,
		itemTotal,
		quoteNumber,
	} = props;

	//confirm modals
	const [discardOrderConfirmOpen, setDiscardOrderConfirmOpen] =
		useState<boolean>(false);

	const sections = [
		{
			label: translation("Items"),
			onClick: goBackToAddItemPage,
			buttonId: "edit-items-button",
		},
		{
			label: translation("Client"),
			onClick: goBackToClientDetail,
			buttonId: "edit-client-info-button",
		},
	];

	const [itemTableHeader, clientTableHeader] = sections.map(
		({ label, onClick, buttonId }) => (
			<div className="review-item-table-header" key={buttonId}>
				<span className="typography-heading-04 typography-bold">{label}</span>
				<TextButton id={buttonId} onClick={onClick} size="xsmall">
					<SmallEditIcon />
					<span className="typography-body-01">{translation("Edit")}</span>
				</TextButton>
			</div>
		)
	);

	const clientInfoTable: React.ReactNode = (
		<div className="item-preview-table-nested card-equal-padding">
			<div className="client-info-review-table-row typography-body-01">
				<div className="client-info-review-table-column ">
					<div>{`${selectedUser.firstName} ${selectedUser.lastName}`}</div>
					<div>{`${selectedUser.clientCompanyName}`}</div>
				</div>
				<div>
					<div>{`${selectedUser.userName}`}</div>
					<div>{`${selectedUser.phoneNumber}`}</div>
				</div>
			</div>
			<Divider sx={{ my: 1 }}></Divider>
			<div className="client-info-review-table-row typography-body-01">
				<div className="client-info-review-table-column">
					<div className="typography-caption typography-bold grey-text">
						{translation("ShipTo")}
					</div>
					<div>{billingInfo.name?.trim() || "--"}</div>
					<div>{billingInfo.email?.trim() || "--"}</div>
					<div>{billingInfo.address?.trim() || "--"}</div>
					<div>
						{[
							billingInfo.city?.trim() || "--",
							billingInfo.province?.trim() || "--",
							billingInfo.country?.trim() || "--",
							billingInfo.postalCode?.trim() || "--",
						].join(", ")}
					</div>
				</div>

				<div className="client-info-review-table-column">
					<div className="typography-caption typography-bold grey-text">
						{translation("BillingTo")}
					</div>
					<div>{shippingInfo.name?.trim() || "--"}</div>
					<div>{shippingInfo.phoneNumber?.trim() || "--"}</div>
					<div>{shippingInfo.address?.trim() || "--"}</div>
					<div>
						{[
							shippingInfo.city?.trim() || "--",
							shippingInfo.province?.trim() || "--",
							shippingInfo.country?.trim() || "--",
							shippingInfo.postalCode?.trim() || "--",
						].join(", ")}
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<>
			{props.licenseLoading ? (
				<NewOrderSkeletonUI />
			) : (
				<div style={{ margin: "0 auto" }}>
					{
						<div className="page-content-padding">
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									paddingBottom: "16px",
								}}
							>
								<div
									className="typography-heading-02 typography-bold"
									style={{ display: "flex", gap: "5px", alignItems: "center" }}
								>
									<CustomIconButton
										id="back-to-order-client-details-button"
										size="small"
										onClick={() => {
											props.goBackToAddItemPage();
										}}
										icon={<KeyboardArrowLeftIconRounded />}
									></CustomIconButton>
									{`${translation("Quote")}: ${quoteNumber}`}
								</div>
								<DropdownButton
									buttonColor="white"
									buttonClassName="new-order-collapible-button"
									id={"new-orders-more-actions"}
									menuOptions={[
										...(itemList.length === 0 || props.isInAdminView
											? []
											: [
													{
														key: "submit-later",
														onClick: () => {
															saveOrderAndEditLater();
														},
														children: (
															<div className="icon-dropdown-menu-item">
																<SaveIcon />
																{translation("SaveAndSubmitLater")}
															</div>
														),
													},
											  ]),
										{
											key: "discard-order",
											onClick: () => {
												setDiscardOrderConfirmOpen(true);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<ClearIcon sx={{ fontSize: "20px" }} />
													{translation("Discardorder")}
												</div>
											),
										},
									]}
									onClick={(e) => {}}
								>
									<span className="new-order-collapsible-text">
										{translation("Action")}
									</span>
								</DropdownButton>
							</div>
							<div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
								<div
									style={{
										flex: "3 3",
										minWidth: "300px",
										display: "flex",
										flexDirection: "column",
										gap: "16px",
									}}
								>
									<div className="item-preview-table-nested">
										{clientTableHeader}
										{clientInfoTable}
									</div>
									<div className="item-preview-table-nested">
										{itemTableHeader}
										<ItemPreviewTable
											itemList={itemList}
											deleteItem={deleteItem}
											goToUpdateItem={goToUpdateItem}
											currency={currency}
											shippingFee={shippingFee}
											handlingFee={handlingFee}
											handlingDescription={props.handlingDescription}
											shippingDescription={props.shippingDescription}
											fullServiceShippingSolution={
												props.fullServiceShippingSolution
											}
											deleteHandlingFee={props.deleteHandlingFee}
											deleteShippingFee={props.deleteShippingFee}
											goToUpdateHandlingShippingFee={goToAddShippingHandling}
											isInNestedCard={true}
											isReadOnlyTable={true}
										/>
									</div>
								</div>
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										flex: "1",
										gap: "16px",
									}}
								>
									<Card
										style={{
											display: "grid",
											padding: "16px 18px",
											minWidth: "150px",
											height: "fit-content",
										}}
									>
										<div
											className="typography-heading-04 typography-bold"
											style={{ paddingBottom: "16px" }}
										>
											{translation("PriceSummary")}
										</div>
										<div className="price-summary-content">
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Item")} x ${
													itemList.length
												}`}</span>
												<span>
													{itemList.length > 0
														? `${formatPrice(itemTotal, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Discount")}${
													discountUnit === DiscountUnit.PERCENTAGE
														? ` (${discount}%)`
														: ""
												}`}</span>
												<span>
													{discount > 0
														? `- ${formatPrice(
																calculateDiscount(
																	itemTotal,
																	discount,
																	discountUnit
																),
																currency
														  )}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Handling")}`}</span>
												<span>
													{handlingFee > 0
														? `${formatPrice(handlingFee, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Shipping")}`}</span>
												<span>
													{shippingFee > 0
														? `${formatPrice(shippingFee, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Subtotal")}`}</span>
												<span>
													{subtotal > 0
														? `${formatPrice(subtotal, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Tax")}`}</span>
												<span>
													{tax > 0 ? `${formatPrice(tax, currency)}` : "--"}
												</span>
											</div>
											<div
												className="typography-body-01 typography-bold price-summary-row"
												style={{ color: "#121212" }}
											>
												<span>{`${translation("Total")}`}</span>
												<span>
													{total > 0 ? `${formatPrice(total, currency)}` : "--"}
												</span>
											</div>
											<BlueButton
												key="submit-order-button"
												id="submit-order-button"
												children={translation("PlaceOrder")}
												size="flex"
												onClick={() => {
													goToSubmitOrder(false);
												}}
											></BlueButton>
										</div>
									</Card>
								</div>
							</div>
						</div>
					}
					<DiscardOrderConfirmModal
						open={discardOrderConfirmOpen}
						setOpen={setDiscardOrderConfirmOpen}
						proceedAction={() => {
							discardOrder();
						}}
					></DiscardOrderConfirmModal>
				</div>
			)}
		</>
	);
}
